import { useState, useRef } from "react";
import { X, UploadCloud, Loader2, Sparkles, AlertCircle, CheckCircle2 } from "lucide-react";
import axios from "axios";

/**
 * Content Effect Importer — modal flow for uploading 1-3 Meta Business Suite
 * screenshots and persisting the extracted metrics into the brand-new
 * `meta_content_overview` collection. Completely isolated from the existing
 * Meta / Facebook / Instagram paid ad pipelines.
 *
 * Flow:
 *  1. Pick client + date range + drop screenshots
 *  2. POST /api/import/content-pulse/extract → returns parsed JSON
 *  3. Render editable confirmation form (FB + IG side-by-side)
 *  4. POST /api/import/content-pulse/save → writes to DB, logs upload
 */

const numberInput = (value, onChange, dataTestId) => (
  <input
    type="number"
    min="0"
    value={value ?? 0}
    onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
    className="w-full px-2 py-1 text-sm border border-slate-300 rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 tabular-nums"
    data-testid={dataTestId}
  />
);

const PlatformPanel = ({ platform, data, onChange, accent }) => {
  const update = (field, value) => onChange({ ...data, [field]: value });
  const updatePost = (i, field, value) => {
    const posts = [...(data.top_posts || [])];
    posts[i] = { ...posts[i], [field]: value };
    update("top_posts", posts);
  };
  const removePost = (i) => {
    const posts = [...(data.top_posts || [])];
    posts.splice(i, 1);
    update("top_posts", posts);
  };

  const heading = platform === "facebook" ? "Facebook" : "Instagram";
  const icon = platform === "facebook" ? "📘" : "📷";

  return (
    <div
      className={`bg-white rounded-xl border-2 p-4 ${accent}`}
      data-testid={`content-effect-platform-${platform}`}
    >
      <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2">
        <span>{icon}</span>{heading}
      </h3>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <Field label="Total Views">{numberInput(data.total_views, (v) => update("total_views", v), `ce-${platform}-views`)}</Field>
        <Field label="Reach">{numberInput(data.reach, (v) => update("reach", v), `ce-${platform}-reach`)}</Field>
        <Field label="Views from Organic">{numberInput(data.organic_views, (v) => update("organic_views", v), `ce-${platform}-organic`)}</Field>
        <Field label="Views from Ads">{numberInput(data.ad_views, (v) => update("ad_views", v), `ce-${platform}-ads`)}</Field>
        <Field label="Content Interactions">{numberInput(data.interactions, (v) => update("interactions", v), `ce-${platform}-interactions`)}</Field>
        <Field label="Profile Visits">{numberInput(data.profile_visits, (v) => update("profile_visits", v), `ce-${platform}-visits`)}</Field>
        <Field label="New Follows">{numberInput(data.new_follows, (v) => update("new_follows", v), `ce-${platform}-follows`)}</Field>
        <Field label="Link Clicks">{numberInput(data.link_clicks, (v) => update("link_clicks", v), `ce-${platform}-clicks`)}</Field>
        <Field label="Reels Views">{numberInput(data.reels_views, (v) => update("reels_views", v), `ce-${platform}-reels`)}</Field>
        <Field label="Stories Views">{numberInput(data.stories_views, (v) => update("stories_views", v), `ce-${platform}-stories`)}</Field>
      </div>

      <div className="mt-4">
        <p className="text-xs font-bold text-slate-700 mb-2">Top Posts ({(data.top_posts || []).length})</p>
        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
          {(data.top_posts || []).map((p, i) => (
            <div key={i} className="bg-slate-50 rounded-md p-2 text-xs space-y-1 border border-slate-200">
              <div className="flex items-start gap-2">
                <input
                  type="text"
                  value={p.title || ""}
                  onChange={(e) => updatePost(i, "title", e.target.value)}
                  className="flex-1 px-2 py-1 border border-slate-300 rounded-md"
                  placeholder="Post title"
                  data-testid={`ce-${platform}-post-${i}-title`}
                />
                <button
                  onClick={() => removePost(i)}
                  className="text-rose-500 hover:text-rose-700 px-1.5 text-base leading-none"
                  data-testid={`ce-${platform}-post-${i}-remove`}
                  title="Remove post"
                >×</button>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                <input
                  type="text"
                  value={p.date || ""}
                  onChange={(e) => updatePost(i, "date", e.target.value)}
                  className="px-1.5 py-1 border border-slate-300 rounded-md tabular-nums"
                  placeholder="Date"
                />
                <input
                  type="number"
                  min="0"
                  value={p.views ?? 0}
                  onChange={(e) => updatePost(i, "views", parseInt(e.target.value || "0", 10))}
                  className="px-1.5 py-1 border border-slate-300 rounded-md tabular-nums"
                  placeholder="Views"
                />
                <input
                  type="number"
                  min="0"
                  value={p.likes ?? 0}
                  onChange={(e) => updatePost(i, "likes", parseInt(e.target.value || "0", 10))}
                  className="px-1.5 py-1 border border-slate-300 rounded-md tabular-nums"
                  placeholder="Likes"
                />
                <input
                  type="number"
                  min="0"
                  value={p.comments ?? 0}
                  onChange={(e) => updatePost(i, "comments", parseInt(e.target.value || "0", 10))}
                  className="px-1.5 py-1 border border-slate-300 rounded-md tabular-nums"
                  placeholder="Comments"
                />
              </div>
            </div>
          ))}
          {(data.top_posts || []).length === 0 && (
            <p className="text-xs text-slate-400 italic">No top posts extracted.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 block mb-0.5">{label}</label>
    {children}
  </div>
);

const blankPlatform = (platform) => ({
  platform,
  total_views: 0, organic_views: 0, ad_views: 0,
  reach: 0, interactions: 0, profile_visits: 0,
  new_follows: 0, link_clicks: 0,
  reels_views: 0, stories_views: 0,
  top_posts: [],
});

export default function ContentEffectImporter({ open, onClose, clients, API, onSaved }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState("upload"); // upload | extracting | confirm | saving | done
  const [clientId, setClientId] = useState("");
  const [reportStart, setReportStart] = useState("");
  const [reportEnd, setReportEnd] = useState("");
  const [locationName, setLocationName] = useState("");
  const [files, setFiles] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [platforms, setPlatforms] = useState([]); // editable extracted state
  const [periodLabel, setPeriodLabel] = useState("");

  const selectedClient = clients.find((c) => c.id === clientId);
  const isMulti = selectedClient?.client_type === "multi";
  const locations = selectedClient?.locations || [];

  const reset = () => {
    setStep("upload");
    setClientId("");
    setReportStart("");
    setReportEnd("");
    setLocationName("");
    setFiles([]);
    setErrorMsg("");
    setPlatforms([]);
    setPeriodLabel("");
  };

  const onSelectFiles = (selected) => {
    const arr = Array.from(selected || []).slice(0, 5);
    setFiles(arr);
  };

  const runExtract = async () => {
    if (!clientId) return setErrorMsg("Pick a client first.");
    if (!reportStart || !reportEnd) return setErrorMsg("Pick a report start and end date.");
    if (files.length === 0) return setErrorMsg("Drop at least one screenshot.");
    setErrorMsg("");
    setStep("extracting");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await axios.post(
        `${API}/import/content-pulse/extract?client_id=${clientId}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 },
      );
      const data = res.data || {};
      const extracted = Array.isArray(data.platforms) ? data.platforms : [];
      // Always ensure both FB + IG are present in the editor (even if blank)
      const byKey = Object.fromEntries(extracted.map((p) => [p.platform, p]));
      const merged = [
        byKey.facebook || blankPlatform("facebook"),
        byKey.instagram || blankPlatform("instagram"),
      ];
      setPlatforms(merged);
      setPeriodLabel(data.report_period_label || "");
      setStep("confirm");
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || e.message || "Extraction failed");
      setStep("upload");
    }
  };

  const runSave = async () => {
    setErrorMsg("");
    setStep("saving");
    try {
      const payload = {
        client_id: clientId,
        report_start: reportStart,
        report_end: reportEnd,
        location_name: locationName || "",
        // Only include platforms that have at least 1 metric > 0
        platforms: platforms.filter((p) =>
          (p.total_views || 0) + (p.organic_views || 0) + (p.ad_views || 0) +
          (p.reach || 0) + (p.interactions || 0) + (p.profile_visits || 0) +
          (p.new_follows || 0) + (p.link_clicks || 0) +
          (p.reels_views || 0) + (p.stories_views || 0) > 0
        ),
      };
      if (payload.platforms.length === 0) {
        setErrorMsg("Nothing to save — all metrics are zero. Edit values or cancel.");
        setStep("confirm");
        return;
      }
      await axios.post(`${API}/import/content-pulse/save`, payload, { timeout: 30000 });
      setStep("done");
      if (onSaved) onSaved();
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || e.message || "Save failed");
      setStep("confirm");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="content-effect-importer-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-indigo-500 flex items-center justify-center text-white">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">The Content Effect</p>
              <p className="text-xs text-slate-500">Drop Meta Business Suite screenshots — AI extracts the metrics for you.</p>
            </div>
          </div>
          <button
            onClick={() => { reset(); onClose(); }}
            className="p-2 rounded-lg hover:bg-slate-100"
            data-testid="content-effect-close"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {errorMsg && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {step === "upload" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Client</label>
                  <select
                    value={clientId}
                    onChange={(e) => { setClientId(e.target.value); setLocationName(""); }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="ce-client-select"
                  >
                    <option value="">Select client…</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Report Start</label>
                  <input
                    type="date"
                    value={reportStart}
                    onChange={(e) => setReportStart(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="ce-report-start"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Report End</label>
                  <input
                    type="date"
                    value={reportEnd}
                    onChange={(e) => setReportEnd(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="ce-report-end"
                  />
                </div>
              </div>

              {isMulti && locations.length > 0 && (
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Location (optional, leave blank for whole-brand)</label>
                  <select
                    value={locationName}
                    onChange={(e) => setLocationName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="ce-location-select"
                  >
                    <option value="">Whole brand</option>
                    {locations.map((l, i) => (
                      <option key={i} value={l.name || l}>{l.name || l}</option>
                    ))}
                  </select>
                </div>
              )}

              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onSelectFiles(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-emerald-500 hover:bg-emerald-50/40 transition cursor-pointer"
                data-testid="ce-drop-zone"
              >
                <UploadCloud className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                <p className="text-sm font-medium text-slate-700">
                  {files.length > 0
                    ? `${files.length} file${files.length > 1 ? "s" : ""} ready`
                    : "Drop FB + IG screenshots here, or click to browse"}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Up to 5 PNG/JPG files · usually 1 FB tab + 1 IG tab + the IG metrics overview page
                </p>
                {files.length > 0 && (
                  <ul className="text-xs text-slate-600 mt-3 space-y-0.5 inline-block text-left">
                    {files.map((f, i) => (<li key={i}>· {f.name} ({(f.size/1024).toFixed(0)} KB)</li>))}
                  </ul>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => onSelectFiles(e.target.files)}
                  data-testid="ce-file-input"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { reset(); onClose(); }}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                >Cancel</button>
                <button
                  onClick={runExtract}
                  disabled={!clientId || !reportStart || !reportEnd || files.length === 0}
                  className="px-5 py-2 text-sm rounded-lg bg-gradient-to-br from-fuchsia-500 to-indigo-500 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 flex items-center gap-2"
                  data-testid="ce-extract-btn"
                >
                  <Sparkles className="w-4 h-4" /> Extract with AI
                </button>
              </div>
            </div>
          )}

          {step === "extracting" && (
            <div className="py-16 text-center" data-testid="ce-extracting">
              <Loader2 className="w-10 h-10 mx-auto text-indigo-500 animate-spin mb-3" />
              <p className="text-sm font-medium text-slate-700">Reading your screenshots…</p>
              <p className="text-xs text-slate-500 mt-1">This usually takes 5-15 seconds.</p>
            </div>
          )}

          {step === "confirm" && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs">
                <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold mb-0.5">Review extracted numbers</p>
                  <p>AI extracted these from your screenshots{periodLabel ? ` (period detected: ${periodLabel})` : ""}. Edit any number that does not match what you see in Meta. Empty platforms get skipped.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {platforms.map((p, i) => (
                  <PlatformPanel
                    key={p.platform}
                    platform={p.platform}
                    data={p}
                    onChange={(updated) => {
                      const next = [...platforms];
                      next[i] = updated;
                      setPlatforms(next);
                    }}
                    accent={p.platform === "facebook" ? "border-blue-200" : "border-fuchsia-200"}
                  />
                ))}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setStep("upload")}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                >Back</button>
                <button
                  onClick={runSave}
                  className="px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 flex items-center gap-2"
                  data-testid="ce-save-btn"
                >
                  <CheckCircle2 className="w-4 h-4" /> Looks Good — Save
                </button>
              </div>
            </div>
          )}

          {step === "saving" && (
            <div className="py-16 text-center" data-testid="ce-saving">
              <Loader2 className="w-10 h-10 mx-auto text-emerald-500 animate-spin mb-3" />
              <p className="text-sm font-medium text-slate-700">Saving Content Effect data…</p>
            </div>
          )}

          {step === "done" && (
            <div className="py-12 text-center" data-testid="ce-done">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-base font-bold text-slate-900 mb-1">Saved!</p>
              <p className="text-sm text-slate-600 mb-5">Open the client&apos;s report to see the new Content Effect section.</p>
              <div className="flex justify-center gap-2">
                <button
                  onClick={reset}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="ce-import-another"
                >Import Another</button>
                <button
                  onClick={() => { reset(); onClose(); }}
                  className="px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                  data-testid="ce-done-close"
                >Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
